// AI-First Intent Classification for Chat
// NO hardcoded patterns - the AI understands context and meaning

import { ChatIntent } from './types';
import { generateIntentDetection } from '@/services/ai/aiGateway';

interface ConversationMessage {
    role: 'user' | 'chef';
    content: string;
}

/**
 * AI-First Intent Classification
 *
 * Uses the LLM to understand the user's intent from:
 * 1. The current message
 * 2. The conversation history (for context like "let's do that")
 * 3. The recipe being discussed (optional - lab assistant passes no recipe)
 *
 * NO regex patterns - the AI understands meaning naturally.
 */
export async function classifyChatIntent(
    message: string,
    recipeTitle: string,
    conversationHistory: ConversationMessage[] = []
): Promise<ChatIntent> {
    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
        return 'CHAT';
    }

    // Build conversation context for the AI
    const historyContext = conversationHistory.length > 0
        ? conversationHistory.slice(-6).map(m =>
            `${m.role === 'user' ? 'User' : 'Chef'}: ${m.content}`
        ).join('\n')
        : '(No prior conversation)';

    const prompt = `You are an intent classifier for a recipe chat assistant.

RECIPE: "${recipeTitle}"

CONVERSATION HISTORY:
${historyContext}

CURRENT USER MESSAGE:
"${trimmedMessage}"

---

TASK: Classify the user's intent. BE CONSERVATIVE - when in doubt, choose QUESTION.

**QUESTION** (default if unsure) - The user is EXPLORING or ASKING:
- Contains a question mark (?)
- Uses "what", "how", "why", "could we", "would it"
- Asks about possibilities: "what would happen if...", "what would we need to change"
- Seeks information before deciding: "could we make this X?"
- Even "could we add X?" is a QUESTION if they're exploring options

**MODIFY** - The user gives a CLEAR COMMAND to change NOW:
- Definite statements: "add garlic", "remove the sugar", "make it vegan"
- Direct instructions without questions: "change it to...", "update the recipe"
- Confirmations AFTER discussing: "yes do that", "let's do it", "go ahead", "make the change"
- NO question marks, NO "what would" phrasing

**CHAT** - Casual conversation:
- "thanks!", "this looks great", "hello"

---

EXAMPLES:
- "could we make this a desert bread? what would we need to change?" → QUESTION
- "how do we make this more like a baguette?" → QUESTION
- "what if we added semolina?" → QUESTION
- "can we add semolina?" → QUESTION (still exploring)
- "add semolina to the recipe" → MODIFY (clear command)
- "yes, make that change" → MODIFY (confirming previous discussion)
- "let's do it" → MODIFY (confirming)

---

Respond with ONLY one word: QUESTION, MODIFY, or CHAT`;

    try {
        const result = await generateIntentDetection(prompt, 'classifyChatIntent');

        if (result.success && result.text) {
            const aiResponse = result.text.trim().toUpperCase();


            if (aiResponse.includes('MODIFY')) {
                return 'CONFIRM_MODIFY';
            } else if (aiResponse.includes('QUESTION')) {
                // Sub-classify question type based on content (simple checks, not for intent)
                const lower = trimmedMessage.toLowerCase();
                if (/instead|substitut|replac|alternat/.test(lower)) {
                    return 'QA_SUBSTITUTION';
                }
                if (/how\s+(to|do|long|much)|technique|method/.test(lower)) {
                    return 'QA_TECHNIQUE';
                }
                if (/what\s+is|what\s+are|why\s+(is|does|do)/.test(lower)) {
                    return 'QA_INGREDIENT';
                }
                return 'QA_GENERAL';
            } else if (aiResponse.includes('CHAT')) {
                return 'CHAT';
            }
        }
    } catch {
    }

    // Safe fallback - treat as question so we don't accidentally charge
    return 'QA_GENERAL';
}

/**
 * Check if an intent requires showing a confirmation modal before executing.
 */
export function requiresConfirmation(intent: ChatIntent): boolean {
    return intent === 'CONFIRM_MODIFY';
}

/**
 * Check if an intent should be free (no coin charge).
 */
export function isFreeIntent(intent: ChatIntent): boolean {
    return intent !== 'CONFIRM_MODIFY';
}

/**
 * Get a human-readable description of the intent.
 */
export function getIntentDescription(intent: ChatIntent): string {
    const descriptions: Record<ChatIntent, string> = {
        'QA_SUBSTITUTION': 'Asking about substitutions',
        'QA_TECHNIQUE': 'Asking about technique',
        'QA_INGREDIENT': 'Asking about ingredients',
        'QA_GENERAL': 'General question',
        'CONFIRM_MODIFY': 'Request to modify recipe',
        'CHAT': 'Casual conversation'
    };
    return descriptions[intent] || 'Unknown';
}
